import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, CheckCircle } from 'lucide-react';
import { experience } from '../data/mock';
import { Card } from './ui/card';

const Experience = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  return (
    <section id="experience" className="section-padding bg-[#0f0f0f]">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="section-title">Professional Journey</h2>
          <p className="section-subtitle mx-auto">
            Building production-grade mobile systems with ownership and impact
          </p>
        </motion.div>

        {/* Experience Items */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="space-y-6"
        >
          {experience.map((exp, index) => (
            <motion.div
              key={exp.id}
              variants={itemVariants}
              className="relative"
            >
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#404040] p-6 md:p-8 transition-all duration-300">
                <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                  {/* Timeline Dot - Hidden on mobile, visible on desktop */}
                  <div className="hidden md:block relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white to-gray-400 flex items-center justify-center">
                      <Briefcase size={20} className="text-black" />
                    </div>
                    {/* Connector line */}
                    {index !== experience.length - 1 && (
                      <div className="absolute left-6 top-12 bottom-0 w-px bg-gradient-to-b from-[#404040] to-transparent transform -translate-x-1/2" style={{ height: 'calc(100% + 24px)' }} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-grow">
                    {/* Header */}
                    <div className="mb-4">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-[#2a2a2a] text-gray-400 text-xs monospace uppercase tracking-wide rounded-full">
                          {exp.type}
                        </span>
                        {/* Mobile icon */}
                        <div className="md:hidden w-8 h-8 rounded-full bg-gradient-to-br from-white to-gray-400 flex items-center justify-center">
                          <Briefcase size={14} className="text-black" />
                        </div>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white">{exp.role}</h3>
                      <p className="text-lg md:text-xl text-gray-300 mb-3 font-medium">{exp.company}</p>
                      <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-gray-400">
                        <div className="flex items-center gap-2">
                          <Clock size={16} />
                          <span className="font-medium">{exp.duration}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} />
                          <span>{exp.location}</span>
                        </div>
                      </div>
                    </div>

                    {/* Impact Section */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider flex items-center gap-2">
                        <CheckCircle size={16} />
                        Key Impact
                      </h4>
                      <ul className="space-y-2 md:space-y-3">
                        {exp.impact.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-sm md:text-base text-gray-300">
                            <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></div>
                            <span className="leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Ownership Tags */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                        Ownership Areas
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {exp.ownership.map((area, idx) => (
                          <span
                            key={idx}
                            className="px-3 md:px-4 py-1.5 md:py-2 bg-[#2a2a2a] text-gray-300 rounded-lg text-xs md:text-sm monospace hover:bg-[#353535] transition-colors"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
